import { forwardRef, RefObject } from 'react';

import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { ArrowLeftOutline, ArrowRightOutline } from '@aslanpay/portal-common-lib';

import useCezanneCompanySync from 'app/hooks/useCezanneCompanySync';

import { cezanneCredentialsFormSchema } from 'app/utils/schemas';

import { CezanneCredentialsFormFields, CezanneCredentialsFormProps } from './types';

/** Step 1 of the Connect-Cezanne wizard: collects raw tenant credentials and synchronously reads the tenant -
 * nothing is created yet at this point, per ASD-4330's acceptance criteria. */
const CezanneCredentialsForm = forwardRef<HTMLElement, CezanneCredentialsFormProps>(
  ({ id, onSynced, onBack }, ref) => {
    const {
      register,
      handleSubmit,
      formState: { errors, isValid },
    } = useForm<CezanneCredentialsFormFields>({
      mode: 'onBlur',
      resolver: yupResolver(cezanneCredentialsFormSchema),
    });

    const {
      mutate: sync,
      isLoading,
      isError,
    } = useCezanneCompanySync({
      onSuccess: (result, credentials) => onSynced(result, credentials),
    });

    const handleSync = (data: CezanneCredentialsFormFields) => sync(data);

    return (
      <form id={id} onSubmit={handleSubmit(handleSync)} noValidate>
        <Stack spacing="20px">
          <FormControl isInvalid={!!errors?.baseUrl?.message} isRequired>
            <FormLabel>Base URL</FormLabel>
            <Input
              type="text"
              placeholder="https://tenant.cezannehr.com/cezanneondemand/v7/dataservice.svc"
              {...register('baseUrl')}
            />
            <FormErrorMessage>{errors?.baseUrl?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.authTokenUrl?.message} isRequired>
            <FormLabel>Token URL</FormLabel>
            <Input
              type="text"
              placeholder="https://tenant.cezannehr.com/CezanneOnDemand/OAuth/Token"
              {...register('authTokenUrl')}
            />
            <FormErrorMessage>{errors?.authTokenUrl?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.authClientId?.message} isRequired>
            <FormLabel>Client ID</FormLabel>
            <Input type="text" placeholder="Enter client ID" {...register('authClientId')} />
            <FormErrorMessage>{errors?.authClientId?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.clientSecret?.message} isRequired>
            <FormLabel>Client secret</FormLabel>
            <Input
              type="password"
              autoComplete="new-password"
              placeholder="Enter client secret"
              {...register('clientSecret')}
            />
            <FormErrorMessage>{errors?.clientSecret?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.authScope?.message} isRequired>
            <FormLabel>Scope</FormLabel>
            <Input type="text" placeholder="Enter scope" {...register('authScope')} />
            <FormHelperText>
              Space-delimited, e.g. one read scope and one write scope.
            </FormHelperText>
            <FormErrorMessage>{errors?.authScope?.message}</FormErrorMessage>
          </FormControl>
          {isError && (
            <Text textStyle="extraSmall" color="system.error">
              Failed to start the sync. Check the credentials and try again.
            </Text>
          )}
          <Portal containerRef={ref as RefObject<HTMLElement>}>
            {onBack && (
              <Button
                size="sm"
                variant="outline"
                leftIcon={<ArrowLeftOutline boxSize="20px" />}
                onClick={onBack}
                mr="16px"
              >
                Back
              </Button>
            )}
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid || isLoading}
              isLoading={isLoading}
              rightIcon={<ArrowRightOutline boxSize="20px" />}
            >
              Sync
            </Button>
          </Portal>
        </Stack>
      </form>
    );
  }
);

CezanneCredentialsForm.displayName = 'CezanneCredentialsForm';

export default CezanneCredentialsForm;
